import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import TrainingPage from './pages/Training';
import DashboardPage from './pages/Dashboard';
import MissionPage from './pages/Mission';
import AnalyticsPage from './pages/Analytics';
import SequenceMissionPage from './pages/SequenceMission';
import DebugPanel from './components/common/DebugPanel';
import LearningDebugPanel from './components/common/LearningDebugPanel';
import MainNav from './components/common/MainNav';
import OnboardingModal from './components/common/OnboardingModal';
import ErrorBoundary from './components/common/ErrorBoundary';
import PWAInstallPrompt from './components/common/PWAInstallPrompt';
import { ProgressProvider } from './context/ProgressContext';
import { LearningProvider } from './context/LearningContext';
import DailyTrainingPage from './pages/DailyTraining';
import { isDebugEnabled } from './config/environment';
import { useScrollToTopOnChange } from './hooks/useScrollToTopOnChange';
import { useOnboarding } from './hooks/useOnboarding';
import './styles/index.css';

/**
 * Main App component for Chess Codebreaker
 * Serves as the root component for the application
 */
const ScrollToTopOnRouteChange: React.FC = () => {
  const location = useLocation();

  useScrollToTopOnChange(location.pathname);

  return null;
};

const App: React.FC = () => {
  // Determine whether to show the debug panel using the environment configuration
  const showDebugPanel = isDebugEnabled();
  
  // Onboarding state management
  const { 
    isOnboardingOpen, 
    completeOnboarding, 
    closeOnboarding,
    openOnboarding
  } = useOnboarding();

  const handleOnboardingClose = () => {
    completeOnboarding();
    closeOnboarding();
  };

  const handleOpenOnboarding = () => {
    openOnboarding();
  };

  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTopOnRouteChange />
        <LearningProvider>
          <ProgressProvider>
            {showDebugPanel && (
              <>
                <DebugPanel />
                <LearningDebugPanel />
              </>
            )}
            <MainNav onOpenOnboarding={handleOpenOnboarding} />
            <ErrorBoundary>
              <div className="main-content" data-testid="main-content">
                <Routes>
                  <Route path="/dashboard" element={
                    <ErrorBoundary>
                      <DashboardPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/analytics" element={
                    <ErrorBoundary>
                      <AnalyticsPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/mission/sequence/:missionId" element={
                    <ErrorBoundary>
                      <SequenceMissionPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/mission/:missionId" element={
                    <ErrorBoundary>
                      <MissionPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/training/daily" element={
                    <ErrorBoundary>
                      <DailyTrainingPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/training" element={
                    <ErrorBoundary>
                      <TrainingPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </div>
            </ErrorBoundary>
            
            {/* Onboarding Modal */}
            <ErrorBoundary>
              <OnboardingModal 
                isOpen={isOnboardingOpen}
                onClose={handleOnboardingClose}
              />
            </ErrorBoundary>
            
            {/* PWA Install Prompt */}
            <ErrorBoundary>
              <PWAInstallPrompt />
            </ErrorBoundary>
          </ProgressProvider>
        </LearningProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App; 
